import React, { useState } from "react";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { LucideIcon } from "lucide-react";
import SimpleDialog, { openSimpleDialog, SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import { Localizer } from "@/main/react/Types";
import { getSafeToastMessageFromError } from "@/main/common/ApiUtils";
import logger from "@/lib/logger";
import { DESTRUCTIVE_NOTICE_SURFACE_CLASS } from "@/main/react/component/destructiveNoticeSurface";

type YesNoDialogProps = {
    title?: string;
    message?: React.ReactNode;
    yesLabel: string;
    yesIcon: LucideIcon;
    noLabel: string;
    noIcon: LucideIcon;
    destructive?: boolean;
    yesPlaywrightId?: string;
    noPlaywrightId?: string;
    // Required so a failed confirm can show a localized, non-technical banner instead of silently
    // leaving the dialog open with no feedback. Every call site already has a localizer in scope.
    localizer: Localizer;
    onYes?: () => void | Promise<void>;
    onNo?: () => void | Promise<void>;
    onClose?: () => void;
};

function YesNoDialogInner({
                             title,
                             message,
                             yesLabel,
                             yesIcon,
                             noLabel,
                             noIcon,
                             destructive = false,
                             yesPlaywrightId,
                             noPlaywrightId,
                             localizer,
                             onYes,
                             onNo,
                             onClose,
                         }: YesNoDialogProps) {
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    async function handleYes() {
        if (busy) return;
        setActionError(null);
        setBusy(true);
        try {
            await onYes?.();
            onClose?.();
        } catch (e) {
            logger.error("Yes action failed", e);
            setActionError(getSafeToastMessageFromError(e, localizer("dialog.confirm.action_failed")));
        } finally {
            setBusy(false);
        }
    }

    async function handleNo() {
        if (busy) return;
        setActionError(null);
        setBusy(true);
        try {
            await onNo?.();
            onClose?.();
        } catch (e) {
            logger.error("No action failed", e);
            setActionError(getSafeToastMessageFromError(e, localizer("dialog.confirm.action_failed")));
        } finally {
            setBusy(false);
        }
    }

    // return content only — wrapper dialog is provided by the programmatic opener
    return (
        <div className="max-w-md">
            <div className=" mb-4">{message}</div>

            {actionError ? (
                <div
                    role="alert"
                    aria-live="assertive"
                    className={`mb-3 rounded-2xl px-4 py-3 text-sm text-muted-foreground ${DESTRUCTIVE_NOTICE_SURFACE_CLASS}`}
                >
                    {actionError}
                </div>
            ) : null}

            <SimpleDialogFooter>
                <CtaButton
                    type="button"
                    onClick={handleNo}
                    icon={noIcon}
                    text={noLabel}
                    variant="outline"
                    disabled={busy}
                    playwrightId={noPlaywrightId}
                />
                <CtaButton
                    type="button"
                    icon={yesIcon}
                    onClick={handleYes}
                    text={yesLabel}
                    variant={destructive ? "destructive" : "primary"}
                    disabled={busy}
                    playwrightId={yesPlaywrightId}
                />
            </SimpleDialogFooter>
        </div>
    );
}

/**
 * Show helper using SimpleDialog/openSimpleDialog
 */
export function showYesNoDialog(options: YesNoDialogProps) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div >{options.title}</div>}
            width="md"
        >
            <YesNoDialogInner
                {...options}
                onClose={() => {
                    onOpenChange(false);
                    options.onClose?.();
                }}
            />
        </SimpleDialog>
    ));
}
